import { useMemo, useCallback } from "react";
import type {
  SubmitData,
  SignerField,
  DownloadData,
  DocumentField,
} from "@superdoc-dev/esign";

// Re-export types for convenience
export type { SubmitData, SignerField, DownloadData };

// Template field - represents a field placeholder in the document.
// Fields may arrive from the API with either `id` or `group` as the identifier
// (see DocumentField schema: {id, value} or {group, value}).
export interface TemplateField {
  id?: string | number;
  group?: string;
  alias?: string;
  value?: string | string[][];
  type?: "text" | "table";
}

// API response shape for an esign document.
// Field types are intentionally loose to accept generated API schema types
// (e.g. EsignTemplateData, BiometricConsentEsignData) without conversion.
export interface ESignDocumentData {
  url: string;
  fields: (TemplateField | Record<string, unknown>)[];
  signerFields?: (SignerField | Record<string, unknown>)[];
  requiresPageLayout?: boolean;
  fileType?: string;
  signedAt?: string;
  templateType?: string;
}

type UseESignOptions = {
  data: ESignDocumentData | undefined;
  onSubmit?: (data: SubmitData) => void | Promise<void>;
};

function isTableFieldValue(value: unknown): value is string[][] {
  return (
    Array.isArray(value) &&
    value.every(
      (row) =>
        Array.isArray(row) && row.every((cell) => typeof cell === "string")
    )
  );
}

function getDocumentFieldValue(field: TemplateField): DocumentField["value"] {
  if (typeof field.value === "string") return field.value;
  if (isTableFieldValue(field.value)) return field.value;
  if (typeof field.alias === "string") return field.alias;
  return "";
}

export function useESign({ data, onSubmit }: UseESignOptions) {
  const url = data?.url ?? null;

  // Convert API fields to SuperDoc DocumentField format.
  // Table values are intentionally passed as 2D arrays (string[][]), which
  // SuperDoc supports for document fields.
  const documentFields = useMemo(() => {
    if (!data?.fields) return [];

    return data.fields.map((field) => {
      const f = field as TemplateField;
      const id = String(f.id ?? f.group);
      const value = getDocumentFieldValue(f);
      const type = isTableFieldValue(value) ? "table" : f.type;

      return {
        id,
        value,
        type,
      };
    });
  }, [data?.fields]);

  const signerFields = useMemo(() => {
    return (data?.signerFields ?? []) as SignerField[];
  }, [data?.signerFields]);

  const requiresPageLayout = data?.requiresPageLayout ?? false;
  const fileType = data?.fileType ?? "DOCX";
  const signedAt = data?.signedAt ?? null;
  const templateType = data?.templateType ?? null;

  const handleSubmit = useCallback(
    async (submitData: SubmitData) => {
      if (onSubmit) {
        await onSubmit(submitData);
      }
    },
    [onSubmit]
  );

  return {
    url,
    documentFields,
    signerFields,
    requiresPageLayout,
    fileType,
    signedAt,
    templateType,
    handleSubmit,
  };
}
